import { useState } from 'react';

import { useError } from '@client/hooks/useError';
import { useSchedulerQueries } from '@client/queries/useSchedulerQueries';
import { schedulerDatesToTs } from '@client/services/scheduler.service';
import { useLoading } from '@client/store/loading.store';
import {
  CScheduler,
  EErrorType,
  type TSchedulerEntityPaths,
  type ESchedulerPrefix,
  type IObjectId,
  type IScheduler,
  type OptionalSchedulers,
  type RequiredSchedulers,
  type TErrorData,
  type TSchedulers,
} from '@common/types';

export const useAssignedSchedulers = (
  schedulers: RequiredSchedulers & OptionalSchedulers,
) => {
  const [assignedSchedulers, setAssignedSchedulers] = useState<TSchedulers>({
    [CScheduler]: { ...schedulers },
  });

  return { assignedSchedulers, setAssignedSchedulers };
};

export const useSchedulers = <T extends IObjectId>(
  entityId: T['id'],
  entityPath: TSchedulerEntityPaths,
  schedulerType: ESchedulerPrefix,
) => {
  const {
    useEntitySchedulers,
    useCreateScheduler,
    useUpdateScheduler,
    useDeleteScheduler,
    useDeleteEntitySchedulers,
  } = useSchedulerQueries();
  const { setError } = useError();

  // Query for user schedulers
  const { data, refetch, isLoading, isFetching, isError, error } =
    useEntitySchedulers<T>(entityId, entityPath, schedulerType);

  const { loading, setLoading } = useLoading(isLoading || isFetching);

  const createMutation = useCreateScheduler<T>(entityPath);
  const updateMutation = useUpdateScheduler();
  const deleteMutation = useDeleteScheduler();
  const deleteAllMutation = useDeleteEntitySchedulers<T>(entityPath);

  const onDelete = async (entity: IScheduler): Promise<void> => {
    try {
      setLoading(true);
      await deleteMutation.mutateAsync(entity.id as string);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_DELETE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onCreate = async <T extends IObjectId>(
    entityId: T['id'],
    data: IScheduler,
  ): Promise<void> => {
    try {
      setLoading(true);

      // Handle the case where the data is contained generated id.
      delete data.id;
      await createMutation.mutateAsync({
        entityId,
        data: schedulerDatesToTs(data),
      });
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_CREATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onUpdate = async (
    id: Required<IScheduler['id']>,
    data: IScheduler,
  ): Promise<void> => {
    try {
      setLoading(true);
      await updateMutation.mutateAsync({
        id,
        data: schedulerDatesToTs(data),
      });
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_UPDATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteAllAssignedMutation = async <T extends IObjectId>(
    entityId: T['id'],
  ): Promise<void> => {
    try {
      setLoading(true);
      await deleteAllMutation.mutateAsync(entityId);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_DELETE,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    isError,
    error,
    schedulers: data?.schedulers || [],
    total: data?.total || 0,
    refetch,
    onCreate,
    onDelete,
    onUpdate,
    deleteAllAssignedMutation,
  };
};
